import type { RouteLocationNormalized } from 'vue-router';

import { isSsoEnabled } from '../config';
import {
  type CurrentUserDto,
  currentUserQueryKey,
  fetchCurrentUser,
} from '../queries/getCurrentUser';
import { queryClient } from '../queryClient';
import { setSentryUserFromCurrentUser } from '../sentry';
import { ampIsAuthenticated } from '../services/amplifyService';
import { hasDvvLogin } from '../services/authProviderState';
import { setPostLoginRedirect } from '../services/postLoginRedirect';
import {
  getCurrentAuthProvider,
  isWoningNetAuthenticated,
} from '../services/woningNetSsoService';
import { handleRedirectBasedOnRole } from './handleRedirectBasedOnRole';
import { handleRoles } from './handleRoles';
import routes from './routes';

/**
 * Check if user is authenticated via any auth provider (DVV or WoningNet SSO)
 */
const isAuthenticated = async (): Promise<boolean> => {
  // Check current auth provider first
  const currentProvider = getCurrentAuthProvider();

  if (currentProvider === 'woningnet') {
    return await isWoningNetAuthenticated();
  }

  // Default to DVV auth check
  return await ampIsAuthenticated();
};

/**
 * Whether an unauthenticated visit to this route should be sent to WoningNet
 * SSO instead of the DVV login.
 *
 * Only routes that are reached through a WoningNet deep link opt in via meta.
 * A tab that already signed in with DVV keeps going to the DVV login, so a
 * tenant or team member whose session expired is not sent to SSO.
 */
const shouldLoginWithWoningNetSso = (to: RouteLocationNormalized): boolean =>
  to.meta.authProvider === 'woningnet' && isSsoEnabled() && !hasDvvLogin();

export const authGuard = async (
  to: RouteLocationNormalized,
): Promise<object | undefined> => {
  // Skip auth guard for WoningNet routes - they handle their own auth
  if (to.meta.isWoningNetRoute) {
    // eslint-disable-next-line no-undefined
    return undefined;
  }

  // Required auth check
  if (to.meta.requiredAuth) {
    if (!(await isAuthenticated())) {
      if (shouldLoginWithWoningNetSso(to)) {
        // The SSO callback URL is fixed, so remember where to return to.
        setPostLoginRedirect(to.fullPath);
        return { name: routes.woningNet };
      }
      return { name: routes.login };
    }
    if (to.meta.roles) {
      let currentUser: CurrentUserDto | null = null;
      try {
        currentUser = (await queryClient.ensureQueryData({
          queryKey: currentUserQueryKey,
          queryFn: fetchCurrentUser,
        }));
      } catch {
        return { name: routes.login };
      }
      if (currentUser) {
        await setSentryUserFromCurrentUser(currentUser);
      }
      if (!(await handleRoles(to))) {
        return { name: routes.notFound };
      }
      // Only call handleRedirectBasedOnRole if we're on the home route
      // This prevents infinite redirects when we're already on a valid route
      if (to.name === routes.home) {
        return await handleRedirectBasedOnRole(to);
      }
    }
  }

  // Redirect to home if user is logged in and on auth path
  if (
    (to.path.includes('/auth') ||
      to.path === '/home') &&
    (await isAuthenticated())
  ) {
    return { name: routes.home };
  }

  // eslint-disable-next-line no-undefined
  return undefined;
};
